// Design tokens + number formatting for the Pulse section (design_guidelines.json)

export const tokens = (dm) => ({
  bg: dm ? "#0F1115" : "#F9F9F8",
  surface: dm ? "#181A1F" : "#FFFFFF",
  surface2: dm ? "#1F2229" : "#F3F4F1",
  text: dm ? "#F3F4F6" : "#111827",
  border: dm ? "#2D3139" : "#E5E7EB",
  muted: dm ? "#9CA3AF" : "#6B7280",
  brand: "#006874",
  accent: dm ? "#33C6D4" : "#006874",
  brandSoft: dm ? "rgba(51,198,212,0.12)" : "rgba(0,104,116,0.08)",
  up: dm ? "#34D399" : "#047857",
  down: dm ? "#F87171" : "#B91C1C",
  expBg: dm ? "rgba(245,158,11,0.16)" : "#FEF3C7",
  expText: dm ? "#FBBF24" : "#78350F",
});

const locale = (lang) => (lang === "it" ? "it-IT" : "en-US");

// 93_759_300 → "93.8M" / "93,8 Mln"
export const fmtCompact = (v, lang = "en") => {
  if (v === null || v === undefined) return "—";
  const abs = Math.abs(v);
  const nf = (n, d) => new Intl.NumberFormat(locale(lang), {
    minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
  if (abs >= 1e9) return `${nf(v / 1e9, 2)}${lang === "it" ? " Mld" : "B"}`;
  if (abs >= 1e6) return `${nf(v / 1e6, 1)}${lang === "it" ? " Mln" : "M"}`;
  if (abs >= 1e4) return `${nf(v / 1e3, 0)}k`;
  return nf(v, 0);
};

export const fmtNum = (v, lang = "en", digits = 0) => {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat(locale(lang), {
    minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
};

export const fmtPct = (v, lang = "en", signed = true) => {
  if (v === null || v === undefined) return "—";
  const s = new Intl.NumberFormat(locale(lang), {
    minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);
  return `${signed && v > 0 ? "+" : ""}${s}%`;
};

// OECD/Eurostat values carry unit_mult (10^n)
export const applyMult = (v, mult) =>
  v === null || v === undefined ? null : v * Math.pow(10, mult || 0);

export const MONTH_LABELS = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  it: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
};

export const FULL_MONTH_LABELS = {
  en: ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"],
  it: ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
    "agosto", "settembre", "ottobre", "novembre", "dicembre"],
};

export const fmtPeriod = (period, lang = "en") => {
  if (!period) return "—";
  const p = String(period);
  const m = /^(\d{4})-(\d{2})$/.exec(p);
  if (m) return `${MONTH_LABELS[lang][parseInt(m[2], 10) - 1]} ${m[1]}`;
  const q = /^(\d{4})-Q(\d)$/i.exec(p);
  if (q) return `${lang === "it" ? "T" : "Q"}${q[2]} ${q[1]}`;
  return p;
};

// Long form for citations / source chips: "May 2026" / "maggio 2026"
export const fmtPeriodLong = (period, lang = "en") => {
  if (!period) return "—";
  const p = String(period);
  const m = /^(\d{4})-(\d{2})$/.exec(p);
  if (m) return `${FULL_MONTH_LABELS[lang][parseInt(m[2], 10) - 1]} ${m[1]}`;
  const q = /^(\d{4})-Q(\d)$/i.exec(p);
  if (q) return `${lang === "it" ? "T" : "Q"}${q[2]} ${q[1]}`;
  return p;
};
